import React from "react";
import { motion } from "framer-motion";
import { Book, FileText, Globe } from "lucide-react";
import { 
  fadeInUp, 
  staggerContainer, 
} from '../utils/animations';
import { useAdvancedScrollAnimation } from '../hooks/useAdvancedScrollAnimation';

const AboutSection = () => {
  const { sectionRef, triggerRef, isMobile } = useAdvancedScrollAnimation({
    fadeOutOnLeave: true,
    mobileBreakpoint: 768,
    fadeThreshold: 0.3
  });

  // Complex text animation
  const textAnimation = {
    hidden: { opacity: 0, y: 20 },
    visible: (i = 1) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.25, 0.25, 0.75],
        delay: 0.1 * i,
      }
    })
  };

  // Image reveal animation
  const imageReveal = {
    hidden: { 
      clipPath: "polygon(0 0, 0 0, 0 100%, 0% 100%)",
      transition: { duration: 0.8 }
    },
    visible: {
      clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)",
      transition: { 
        duration: 1.2,
        ease: [0.645, 0.045, 0.355, 1]
      }
    }
  };

  return (
    <div ref={triggerRef} className="w-full">
      <motion.section 
        ref={sectionRef}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
        className={`
          py-16 md:py-20 
          bg-white 
          relative 
          overflow-hidden 
          ${isMobile ? 'px-4' : ''}
        `}
      >
        {/* Enhanced background animations */}
        <motion.div
          className="absolute top-0 right-0 w-96 h-96 bg-amber-100/30 rounded-full -translate-y-1/2 translate-x-1/2"
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 90, 0],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        <motion.div
          className="absolute bottom-0 left-0 w-72 h-72 bg-amber-100/20 rounded-full translate-y-1/2 -translate-x-1/2"
          animate={{
            scale: [1, 1.15, 1],
            rotate: [0, -90, 0],
            opacity: [0.2, 0.4, 0.2]
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear"
          }}
        />

        <div className="container mx-auto px-4">
          <div className={`
            grid 
            ${isMobile 
              ? 'grid-cols-1 gap-8' 
              : 'grid-cols-1 lg:grid-cols-2 gap-12'
            } 
            items-center
          `}>
            <motion.div 
              className="about-image relative"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={imageReveal}
            >
              <motion.div
                className="relative"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
              >
                <img 
                  src="/api/placeholder/600/400" 
                  alt="Institute" 
                  className="rounded-lg shadow-2xl w-full h-auto object-cover"
                />
                <motion.div 
                  className="absolute inset-0 bg-gradient-to-r from-amber-500/20 to-transparent rounded-lg"
                  whileHover={{
                    background: "linear-gradient(to right, rgba(245, 158, 11, 0.3), transparent)"
                  }}
                />
              </motion.div>
            </motion.div>

            <div className="about-content">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <motion.h3 
                  custom={1}
                  variants={textAnimation}
                  className={`
                    font-bold mb-6 text-gray-900
                    ${isMobile 
                      ? 'text-2xl sm:text-3xl' 
                      : 'text-3xl md:text-4xl'
                    }
                  `}
                >
                  <motion.span
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                  >
                    The Institute of 
                  </motion.span>{" "}
                  <motion.span
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    className="text-amber-500"
                  >
                    Multidisciplinary Studies
                  </motion.span>
                </motion.h3>
                <motion.p 
                  custom={2}
                  variants={textAnimation}
                  className={`
                    text-gray-600 italic mb-8
                    ${isMobile 
                      ? 'text-base' 
                      : 'text-base md:text-lg'
                    }
                  `}
                >
                  A pioneering institution dedicated to fostering intellectual growth, 
                  driving research excellence, and championing multidisciplinary 
                  collaboration across global challenges.
                </motion.p>
              </motion.div>

              <motion.div 
                className="feature-list space-y-4"
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
              >
                <FeatureItem 
                  icon={<Book />} 
                  text="Advanced Technological Research"
                  delay={0.1}
                />
                <FeatureItem 
                  icon={<FileText />} 
                  text="Economic Policy and Innovation"
                  delay={0.2}
                />
                <FeatureItem 
                  icon={<Globe />} 
                  text="Climate Change and Historical Studies"
                  delay={0.3}
                />
              </motion.div>
            </div>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

// Enhanced Feature Item Component
const FeatureItem = ({ icon, text, delay = 0 }) => {
  return (
    <motion.div
      className={`
        flex items-center 
        space-x-4 
        p-3 md:p-4 
        bg-gray-50 
        rounded-lg 
        hover:bg-white 
        hover:shadow-md 
        transition-all 
        duration-300
      `}
      variants={fadeInUp}
      custom={delay}
      whileHover={{ 
        x: 10, 
        y: -5,
        backgroundColor: "#ffffff",
        boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)"
      }}
      whileTap={{ scale: 0.98 }}
      transition={{ 
        type: "spring", 
        stiffness: 300,
        delay: delay
      }}
    >
      <motion.div 
        className="text-amber-500 flex-shrink-0"
        whileHover={{ 
          rotate: 360,
          scale: 1.2
        }}
        transition={{ 
          duration: 0.6,
          type: "spring",
          stiffness: 200
        }}
      >
        {React.cloneElement(icon, { size: 24 })}
      </motion.div>
      <motion.span 
        className="text-gray-700 text-sm md:text-base"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: delay + 0.2 }}
      >
        {text}
      </motion.span>
    </motion.div>
  );
};

export default AboutSection;